import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Calendar, CalendarPlus, Clock, Loader2, AlertTriangle, ChevronDown, ChevronUp, PawPrint, User, CheckCircle, XCircle, Copy, Check } from 'lucide-react';
import { getUpcomingAppointments, getPreviousAppointments, getAppointmentPatterns, getAvailableSlots, bookAppointment, fetchTicketDetails, cancelAppointment } from '../services/daysmartService';
import AppointmentDetailsModal from './modals/AppointmentDetailsModal';
import AvailabilityPanel from './AvailabilityPanel';

/**
 * Reusable panel that shows a client's past and future appointments.
 * Designed to be embedded alongside other content (e.g., communication chain timeline).
 *
 * Props:
 *   clientId    - DaySmart client ID (required to fetch)
 *   locationId  - DaySmart location ID (1=Hopkins, 2=Inver Grove)
 *   clientName  - Display name (optional, shown in header)
 *   maxPast     - Max number of past appointments to show (default 3)
 *   compact     - If true, uses smaller text/padding
 */
const ClientAppointmentsPanel = ({ clientId, locationId, clientName, maxPast = 3, compact = false, onOpenBooking }) => {
    const [upcoming, setUpcoming] = useState([]);
    const [previous, setPrevious] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showAllPast, setShowAllPast] = useState(false);
    const [selectedPets, setSelectedPets] = useState(null);
    const [copied, setCopied] = useState(false);

    // Booking state
    const [showBooking, setShowBooking] = useState(false);
    const [patterns, setPatterns] = useState(null);
    const [patternsLoading, setPatternsLoading] = useState(false);
    const [selectedSuggestion, setSelectedSuggestion] = useState(null);
    const [availableSlots, setAvailableSlots] = useState([]);
    const [slotsLoading, setSlotsLoading] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [bookingState, setBookingState] = useState('idle'); // idle, booking, success, error
    const [bookingError, setBookingError] = useState(null);
    const [availabilitySlot, setAvailabilitySlot] = useState(null); // slot picked from AvailabilityPanel

    // Appointment details modal state
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [ticketDetails, setTicketDetails] = useState(null);
    const [ticketLoading, setTicketLoading] = useState(false);
    const [ticketError, setTicketError] = useState(null);

    const fetchAppointments = useCallback(async () => {
        if (!clientId || !locationId) return;
        setLoading(true);
        setError(null);

        try {
            const [upRes, prevRes] = await Promise.all([
                getUpcomingAppointments(clientId, locationId).catch(err => {
                    console.error('Failed to fetch upcoming:', err);
                    return { appointments: [] };
                }),
                getPreviousAppointments(clientId, locationId).catch(err => {
                    console.error('Failed to fetch previous:', err);
                    return { appointments: [] };
                }),
            ]);
            setUpcoming(upRes.appointments || []);
            setPrevious(prevRes.appointments || []);
        } catch (err) {
            console.error('Error fetching client appointments:', err);
            setError(err.message || 'Failed to load appointments');
        } finally {
            setLoading(false);
        }
    }, [clientId, locationId]);

    useEffect(() => {
        fetchAppointments();
    }, [fetchAppointments]);

    // --- Booking functions ---
    const handleOpenBooking = async () => {
        setShowBooking(true);
        setSelectedSuggestion(null);
        setAvailableSlots([]);
        setSelectedSlot(null);
        setAvailabilitySlot(null);
        setBookingState('idle');
        setBookingError(null);

        if (!patterns && !patternsLoading) {
            setPatternsLoading(true);
            try {
                const result = await getAppointmentPatterns(clientId, locationId);
                setPatterns(result);
            } catch (err) {
                console.error('Failed to fetch patterns:', err);
            } finally {
                setPatternsLoading(false);
            }
        }
    };

    const handleSelectSuggestion = async (suggestion) => {
        setSelectedSuggestion(suggestion);
        setSelectedSlot(null);
        setAvailableSlots([]);
        setSlotsLoading(true);

        try {
            const petPattern = suggestion.pet_pattern || {};
            const result = await getAvailableSlots(
                locationId,
                suggestion.suggested_date,
                petPattern.preferred_employee_id || null,
                petPattern.preferred_employee_name || null,
                petPattern.typical_duration_minutes || 60,
                true
            );
            setAvailableSlots(result.available_slots || []);
        } catch (err) {
            console.error('Failed to fetch slots:', err);
            setAvailableSlots([]);
        } finally {
            setSlotsLoading(false);
        }
    };

    const handleBookAppointment = async () => {
        // Support booking from either pattern suggestions or availability picker
        const slot = selectedSlot || availabilitySlot;
        if (!slot) return;

        setBookingState('booking');
        setBookingError(null);

        try {
            const petPattern = selectedSuggestion?.pet_pattern || {};
            // Fall back to detected context from history when no pattern suggestion
            const ctx = detectedBookingContext || {};
            await bookAppointment({
                location_id: locationId,
                client_id: clientId,
                pet_id: petPattern.pet_id || ctx.pet_id || undefined,
                pet_name: petPattern.pet_name || ctx.pet_name || undefined,
                employee_id: slot.employee_id,
                start_datetime: slot.start_time,
                end_datetime: slot.end_time,
                service_name: petPattern.typical_service || ctx.service_name || 'Grooming',
                service_id: ctx.service_id || undefined,
                additional_services: ctx.additional_services || undefined,
                additional_pets: ctx.additional_pets?.map(p => ({
                    ...p,
                    start_datetime: slot.start_time,
                    end_datetime: slot.end_time,
                })) || undefined,
            });
            setBookingState('success');
            // Refresh appointments list after booking
            fetchAppointments();
        } catch (err) {
            console.error('Failed to book appointment:', err);
            setBookingState('error');
            setBookingError(err.message || 'Failed to book appointment');
        }
    };

    // --- Appointment detail modal ---
    const handleAppointmentCardClick = async (group) => {
        if (!group.ticketId || !locationId) return;
        setShowDetailsModal(true);
        setTicketLoading(true);
        setTicketError(null);
        setTicketDetails(null);

        try {
            const result = await fetchTicketDetails(group.ticketId, locationId);
            setTicketDetails(result.ticket);
        } catch (err) {
            setTicketError(err.message);
        } finally {
            setTicketLoading(false);
        }
    };

    const handleCloseDetailsModal = () => {
        setShowDetailsModal(false);
        setTicketDetails(null);
        setTicketError(null);
    };

    const handleCancelAppointment = async (ticketId, locId, comment) => {
        await cancelAppointment(ticketId, locId, false, comment);
        fetchAppointments();
    };

    // --- Helpers ---
    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    };

    const getLocationName = (locId) => {
        return locId === 1 ? 'Hopkins' : locId === 2 ? 'Inver Grove' : '';
    };

    // Consolidate appointments by date (group same-day items)
    const consolidateByDate = (appts) => {
        const byDate = {};
        appts.forEach(appt => {
            const dateKey = appt.start_time
                ? new Date(appt.start_time).toDateString()
                : appt.date_scheduled || 'unknown';
            if (!byDate[dateKey]) byDate[dateKey] = [];
            byDate[dateKey].push(appt);
        });

        return Object.entries(byDate).map(([dateKey, items]) => {
            items.sort((a, b) => new Date(a.start_time || 0) - new Date(b.start_time || 0));
            const petNames = [...new Set(items.map(i => i.pet_name).filter(Boolean))];
            const employeeNames = [...new Set(items.map(i => i.employee_name).filter(Boolean))];
            const services = items.map(i => i.item_description).filter(Boolean);
            const totalDuration = items.reduce((sum, i) => sum + (i.duration || 0), 0);
            return {
                key: dateKey,
                date: items[0].start_time || items[0].date_scheduled,
                petNames,
                employeeNames,
                services,
                ticketId: items[0].ticket_id,
                items,
                totalDuration,
            };
        });
    };

    const consolidatedUpcoming = consolidateByDate(upcoming);
    const consolidatedPrevious = consolidateByDate(previous);

    // Pet filter for upcoming appointments
    const uniqueUpcomingPets = useMemo(() => {
        const pets = new Set();
        consolidatedUpcoming.forEach(g => (g.petNames || []).forEach(p => pets.add(p)));
        return [...pets];
    }, [consolidatedUpcoming]);

    const activeUpcomingPets = useMemo(() => selectedPets ?? new Set(uniqueUpcomingPets), [selectedPets, uniqueUpcomingPets]);

    const filteredUpcoming = useMemo(() => {
        return consolidatedUpcoming.filter(g => (g.petNames || []).some(p => activeUpcomingPets.has(p)));
    }, [consolidatedUpcoming, activeUpcomingPets]);

    const toggleUpcomingPet = (petName) => {
        setSelectedPets(prev => {
            const current = prev ?? new Set(uniqueUpcomingPets);
            const next = new Set(current);
            if (next.has(petName)) next.delete(petName);
            else next.add(petName);
            return next;
        });
    };

    const handleCopyUpcoming = () => {
        const locName = getLocationName(locationId);
        const lines = [locName ? `Zoom Groom - ${locName}` : 'Zoom Groom', `Upcoming Appointments for ${clientName || 'Client'}`, ''];
        if (filteredUpcoming.length > 0) {
            filteredUpcoming.forEach(group => {
                const d = new Date(group.date);
                const dateStr = d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
                const timeStr = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                const pets = group.petNames.join(' & ');
                const services = [...new Set(group.services)].join(', ');
                lines.push(`${dateStr} at ${timeStr} \u2014 ${pets}${services ? ` (${services})` : ''}`);
            });
        } else {
            lines.push('No upcoming appointments scheduled.');
        }
        navigator.clipboard.writeText(lines.join('\n')).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    // Sort: upcoming ascending, previous descending (most recent first)
    consolidatedUpcoming.sort((a, b) => new Date(a.date) - new Date(b.date));
    consolidatedPrevious.sort((a, b) => new Date(b.date) - new Date(a.date));

    const visiblePast = showAllPast ? consolidatedPrevious : consolidatedPrevious.slice(0, maxPast);

    // Detect booking context from the most recent previous appointment
    const detectedBookingContext = useMemo(() => {
        if (!previous || previous.length === 0) return null;

        // Group by date to find the most recent appointment group
        const mostRecent = consolidatedPrevious[0];
        if (!mostRecent || !mostRecent.items || mostRecent.items.length === 0) return null;

        const items = mostRecent.items;
        const totalDuration = items.reduce((sum, i) => sum + (i.duration || 0), 0);

        // Round to nearest 15-minute increment for the dropdown
        const roundedDuration = totalDuration > 0
            ? Math.ceil(totalDuration / 15) * 15
            : null;

        // Group items by pet_id to handle multi-pet appointments
        const petGroups = {};
        for (const item of items) {
            const pid = item.pet_id || 'unknown';
            if (!petGroups[pid]) petGroups[pid] = [];
            petGroups[pid].push(item);
        }
        const petIds = Object.keys(petGroups);

        // Primary pet = first pet encountered (first item's pet)
        const primaryPetId = petIds[0];
        const primaryItems = petGroups[primaryPetId];
        const primaryItem = primaryItems[0];

        // Additional services for the primary pet (same pet, extra service lines)
        // Deduplicate by name (item_description), not item_id — IDs from history can be wrong
        const seenNames = new Set([primaryItem.item_description]);
        const additionalServices = primaryItems.slice(1)
            .filter(i => {
                const desc = i.item_description || '';
                if (!desc || !i.item_id || seenNames.has(desc)) return false;
                seenNames.add(desc);
                return true;
            })
            .map(i => ({
                service_id: i.item_id,
                service_name: i.item_description || '',
                price: i.price || 0,
            }));

        // Additional pets (different pet_id in the same ticket)
        const additionalPets = petIds.slice(1).map(pid => {
            const petItems = petGroups[pid];
            const first = petItems[0];
            const petSeenNames = new Set([first.item_description]);
            const extraSvcs = petItems.slice(1)
                .filter(i => {
                    const desc = i.item_description || '';
                    if (!desc || !i.item_id || petSeenNames.has(desc)) return false;
                    petSeenNames.add(desc);
                    return true;
                })
                .map(i => ({
                    service_id: i.item_id,
                    service_name: i.item_description || '',
                    price: i.price || 0,
                }));
            return {
                pet_id: first.pet_id,
                pet_name: first.pet_name || '',
                employee_id: first.employee_id || primaryItem.employee_id,
                service_id: first.item_id || '',
                service_name: first.item_description || '',
                price: first.price || 0,
                additional_services: extraSvcs.length > 0 ? extraSvcs : undefined,
            };
        });

        return {
            pet_name: primaryItem.pet_name || null,
            pet_id: primaryItem.pet_id || null,
            service_name: primaryItem.item_description || null,
            service_id: primaryItem.item_id || null,
            employee_name: primaryItem.employee_name || null,
            employee_id: primaryItem.employee_id || null,
            duration_minutes: roundedDuration,
            services: items.map(i => i.item_description).filter(Boolean),
            additional_services: additionalServices.length > 0 ? additionalServices : null,
            additional_pets: additionalPets.length > 0 ? additionalPets : null,
        };
    }, [previous, consolidatedPrevious]);

    // --- No client_id guard ---
    if (!clientId) {
        return (
            <div className="flex flex-col h-full overflow-hidden">
                <div className="flex items-center gap-2 text-gray-400 mb-3">
                    <AlertTriangle size={16} />
                    <p className="text-xs">No client ID linked — appointment history unavailable.</p>
                </div>
                <AvailabilityPanel
                    defaultLocationId={locationId}
                    onSlotSelect={() => {}}
                />
            </div>
        );
    }

    const textSm = compact ? 'text-xs' : 'text-sm';
    const textBase = compact ? 'text-sm' : 'text-base';
    const px = compact ? 'px-3' : 'px-4';
    const py = compact ? 'py-2' : 'py-3';

    // --- Appointment card ---
    const AppointmentCard = ({ group, isPast = false }) => (
        <div
            onClick={() => handleAppointmentCardClick(group)}
            className={`bg-white rounded-lg border ${isPast ? 'border-gray-200' : 'border-blue-200'} ${px} ${py} ${compact ? 'mb-1.5' : 'mb-2'} cursor-pointer hover:shadow-sm transition-shadow`}>
            <div className="flex justify-between items-start">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                        <PawPrint size={compact ? 12 : 14} className={isPast ? 'text-gray-400' : 'text-blue-500'} />
                        <span className={`font-medium ${textSm} ${isPast ? 'text-gray-600' : 'text-gray-800'}`}>
                            {group.petNames.join(' & ') || 'Unknown Pet'}
                        </span>
                    </div>
                    {group.services.length > 0 && (
                        <p className={`${compact ? 'text-[11px]' : 'text-xs'} text-gray-500 mt-0.5 truncate`}>
                            {[...new Set(group.services)].join(', ')}
                        </p>
                    )}
                    {group.employeeNames.length > 0 && (
                        <p className={`${compact ? 'text-[11px]' : 'text-xs'} text-gray-400 mt-0.5`}>
                            <User size={10} className="inline mr-0.5" />
                            {group.employeeNames.join(' & ')}
                        </p>
                    )}
                </div>
                <div className="text-right shrink-0 ml-2">
                    <p className={`${textSm} font-medium ${isPast ? 'text-gray-500' : 'text-blue-700'}`}>
                        {formatDate(group.date)}
                    </p>
                    <p className={`${compact ? 'text-[11px]' : 'text-xs'} text-gray-400`}>
                        {formatTime(group.date)}
                        {group.totalDuration > 0 && (
                            <span className="ml-1">({group.totalDuration}m)</span>
                        )}
                    </p>
                </div>
            </div>
        </div>
    );

    return (
        <div className="flex flex-col h-full overflow-hidden">
            <AppointmentDetailsModal
                isOpen={showDetailsModal}
                onClose={handleCloseDetailsModal}
                ticket={ticketDetails}
                loading={ticketLoading}
                error={ticketError}
                viewOnly
                onCancelAppointment={handleCancelAppointment}
            />
            {/* Header */}
            <div className={`flex items-center justify-between ${compact ? 'mb-2' : 'mb-3'}`}>
                <h4 className={`font-semibold ${textBase} text-gray-700 flex items-center gap-1.5`}>
                    <Calendar size={compact ? 14 : 16} />
                    Appointments
                </h4>
                {locationId && (
                    <span className="text-[11px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                        {getLocationName(locationId)}
                    </span>
                )}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
                {loading ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="animate-spin text-blue-500" size={20} />
                        <span className={`ml-2 ${textSm} text-gray-500`}>Loading appointments…</span>
                    </div>
                ) : error ? (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                        {error}
                    </div>
                ) : (
                    <>
                        {/* Upcoming Appointments */}
                        <div className={compact ? 'mb-3' : 'mb-4'}>
                            <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                                <Clock size={12} className="text-blue-500" />
                                <h5 className={`${compact ? 'text-xs' : 'text-sm'} font-semibold text-blue-700 uppercase tracking-wide`}>
                                    Upcoming ({filteredUpcoming.length})
                                </h5>
                                {uniqueUpcomingPets.length > 1 && (
                                    <div className="flex items-center gap-2 ml-auto">
                                        {uniqueUpcomingPets.map(pet => (
                                            <label key={pet} className="flex items-center gap-1 text-[11px] text-gray-600 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={activeUpcomingPets.has(pet)}
                                                    onChange={() => toggleUpcomingPet(pet)}
                                                    className="rounded text-blue-600 focus:ring-blue-500 h-3 w-3"
                                                />
                                                {pet}
                                            </label>
                                        ))}
                                    </div>
                                )}
                                <button
                                    onClick={handleCopyUpcoming}
                                    className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium transition-colors ml-1 ${copied ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                    title="Copy upcoming appointments to clipboard"
                                >
                                    {copied ? <Check size={11} /> : <Copy size={11} />}
                                    {copied ? 'Copied!' : 'Copy'}
                                </button>
                            </div>
                            {filteredUpcoming.length > 0 ? (
                                filteredUpcoming.map(group => (
                                    <AppointmentCard key={group.key} group={group} />
                                ))
                            ) : (
                                <p className={`${textSm} text-gray-400 italic py-2`}>No upcoming appointments</p>
                            )}
                        </div>

                        {/* Divider */}
                        <hr className="border-gray-200 mb-3" />

                        {/* Past Appointments */}
                        <div>
                            <div className="flex items-center gap-1.5 mb-1.5">
                                <Clock size={12} className="text-gray-400" />
                                <h5 className={`${compact ? 'text-xs' : 'text-sm'} font-semibold text-gray-500 uppercase tracking-wide`}>
                                    Recent ({consolidatedPrevious.length})
                                </h5>
                            </div>
                            {visiblePast.length > 0 ? (
                                <>
                                    {visiblePast.map(group => (
                                        <AppointmentCard key={group.key} group={group} isPast />
                                    ))}
                                    {consolidatedPrevious.length > maxPast && (
                                        <button
                                            onClick={() => setShowAllPast(!showAllPast)}
                                            className="w-full text-center text-xs text-blue-600 hover:text-blue-800 py-1 flex items-center justify-center gap-1"
                                        >
                                            {showAllPast ? (
                                                <>Show less <ChevronUp size={12} /></>
                                            ) : (
                                                <>Show all {consolidatedPrevious.length} <ChevronDown size={12} /></>
                                            )}
                                        </button>
                                    )}
                                </>
                            ) : (
                                <p className={`${textSm} text-gray-400 italic py-2`}>No past appointments found</p>
                            )}
                        </div>

                        {/* Divider before booking */}
                        <hr className="border-gray-200 my-3" />

                        {/* Book Appointment */}
                        {!showBooking ? (
                            <button
                                onClick={() => {
                                    if (onOpenBooking) {
                                        // Open the sticky drawer with booking context
                                        onOpenBooking({
                                            clientId,
                                            clientName,
                                            locationId,
                                            pet_name: detectedBookingContext?.pet_name || null,
                                            pet_id: detectedBookingContext?.pet_id || null,
                                            service_name: detectedBookingContext?.service_name || null,
                                            service_id: detectedBookingContext?.service_id || null,
                                            employee_name: detectedBookingContext?.employee_name || null,
                                            duration_minutes: detectedBookingContext?.duration_minutes || null,
                                            additional_services: detectedBookingContext?.additional_services || null,
                                            additional_pets: detectedBookingContext?.additional_pets || null,
                                        });
                                    } else {
                                        handleOpenBooking();
                                    }
                                }}
                                className="w-full flex items-center justify-center gap-1.5 py-2 px-3 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition-colors"
                            >
                                <CalendarPlus size={14} />
                                Book Appointment
                            </button>
                        ) : (
                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                                <div className="flex items-center justify-between mb-2">
                                    <h5 className="text-sm font-semibold text-purple-800 flex items-center gap-1.5">
                                        <CalendarPlus size={14} />
                                        Book Appointment
                                    </h5>
                                    <button
                                        onClick={() => {
                                            setShowBooking(false);
                                            setBookingState('idle');
                                        }}
                                        className="text-purple-400 hover:text-purple-600"
                                    >
                                        <XCircle size={14} />
                                    </button>
                                </div>

                                {/* Booking success */}
                                {bookingState === 'success' && (
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-2">
                                        <div className="flex items-center gap-2 text-green-700">
                                            <CheckCircle size={16} />
                                            <span className="text-sm font-medium">Appointment booked!</span>
                                        </div>
                                        <button
                                            onClick={() => {
                                                setShowBooking(false);
                                                setBookingState('idle');
                                            }}
                                            className="mt-2 text-xs text-green-600 hover:text-green-800 underline"
                                        >
                                            Close
                                        </button>
                                    </div>
                                )}

                                {/* Booking error */}
                                {bookingState === 'error' && (
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-2 mb-2 text-xs text-red-700">
                                        {bookingError || 'Booking failed'}
                                        <button
                                            onClick={() => setBookingState('idle')}
                                            className="ml-2 underline"
                                        >
                                            Retry
                                        </button>
                                    </div>
                                )}

                                {/* Pattern loading */}
                                {patternsLoading && (
                                    <div className="flex items-center gap-2 py-3 text-purple-600">
                                        <Loader2 className="animate-spin" size={14} />
                                        <span className="text-xs">Analyzing booking patterns…</span>
                                    </div>
                                )}

                                {/* Suggestions from patterns */}
                                {bookingState !== 'success' && patterns && patterns.suggestions && patterns.suggestions.length > 0 && (
                                    <div className="space-y-1.5">
                                        <p className="text-xs text-purple-700 font-medium mb-1">Suggested appointments:</p>
                                        {patterns.suggestions.map((sug, idx) => {
                                            const isSelected = selectedSuggestion === sug;
                                            const petPat = sug.pet_pattern || {};
                                            return (
                                                <button
                                                    key={idx}
                                                    onClick={() => handleSelectSuggestion(sug)}
                                                    className={`w-full text-left rounded-md p-2 border text-xs transition-colors ${
                                                        isSelected
                                                            ? 'border-purple-400 bg-purple-100'
                                                            : 'border-purple-100 bg-white hover:border-purple-300'
                                                    }`}
                                                >
                                                    <div className="flex justify-between items-start">
                                                        <div>
                                                            <span className="font-medium text-gray-800">
                                                                <PawPrint size={10} className="inline mr-0.5 text-purple-500" />
                                                                {petPat.pet_name || 'Pet'}
                                                            </span>
                                                            {petPat.typical_service && (
                                                                <span className="text-gray-500"> — {petPat.typical_service}</span>
                                                            )}
                                                        </div>
                                                        <span className="text-purple-700 font-medium shrink-0 ml-2">
                                                            {formatDate(sug.suggested_date)}
                                                        </span>
                                                    </div>
                                                    {petPat.preferred_employee_name && (
                                                        <p className="text-gray-400 mt-0.5">
                                                            <User size={9} className="inline mr-0.5" />
                                                            {petPat.preferred_employee_name}
                                                        </p>
                                                    )}
                                                    {sug.confidence && (
                                                        <p className="text-purple-400 mt-0.5">
                                                            Confidence: {Math.round(sug.confidence * 100)}%
                                                        </p>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* No suggestions — show availability picker */}
                                {bookingState !== 'success' && patterns && (!patterns.suggestions || patterns.suggestions.length === 0) && (
                                    <div className="mt-1">
                                        {detectedBookingContext ? (
                                            <div className="bg-white border border-purple-200 rounded-md p-2 mb-2">
                                                <p className="text-xs font-medium text-purple-700 mb-1">Based on history:</p>
                                                <div className="flex items-center gap-1.5 text-xs text-gray-700">
                                                    {detectedBookingContext.pet_name && (
                                                        <span className="flex items-center gap-0.5 font-medium">
                                                            <PawPrint size={10} className="text-purple-500" />
                                                            {detectedBookingContext.pet_name}
                                                        </span>
                                                    )}
                                                    {detectedBookingContext.service_name && (
                                                        <span className="text-gray-500">— {detectedBookingContext.service_name}</span>
                                                    )}
                                                    {detectedBookingContext.duration_minutes && (
                                                        <span className="text-gray-400">
                                                            ({detectedBookingContext.duration_minutes >= 60
                                                                ? `${Math.floor(detectedBookingContext.duration_minutes / 60)}hr${detectedBookingContext.duration_minutes % 60 ? ` ${detectedBookingContext.duration_minutes % 60}m` : ''}`
                                                                : `${detectedBookingContext.duration_minutes}m`})
                                                        </span>
                                                    )}
                                                </div>
                                                {detectedBookingContext.employee_name && (
                                                    <p className="text-xs text-gray-400 mt-0.5">
                                                        <User size={9} className="inline mr-0.5" />
                                                        Last with {detectedBookingContext.employee_name}
                                                    </p>
                                                )}
                                            </div>
                                        ) : (
                                            <p className="text-xs text-purple-500 italic mb-2">
                                                No pattern-based suggestions. Browse available times below:
                                            </p>
                                        )}
                                        <AvailabilityPanel
                                            defaultLocationId={locationId}
                                            defaultDuration={detectedBookingContext?.duration_minutes || undefined}
                                            onSlotSelect={(slot) => {
                                                setAvailabilitySlot(slot);
                                                setSelectedSlot(null);
                                                setSelectedSuggestion(null);
                                            }}
                                        />
                                        {/* Confirm booking button for availability-picked slot */}
                                        {availabilitySlot && (
                                            <button
                                                onClick={handleBookAppointment}
                                                disabled={bookingState === 'booking'}
                                                className={`w-full mt-3 py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${
                                                    bookingState === 'booking'
                                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                        : 'bg-green-600 text-white hover:bg-green-700'
                                                }`}
                                            >
                                                {bookingState === 'booking' ? (
                                                    <>
                                                        <Loader2 className="animate-spin" size={14} />
                                                        Booking…
                                                    </>
                                                ) : (
                                                    <>
                                                        <CheckCircle size={14} />
                                                        Confirm Booking
                                                    </>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                )}

                                {/* Available slots */}
                                {selectedSuggestion && bookingState !== 'success' && (
                                    <div className="mt-2">
                                        {slotsLoading ? (
                                            <div className="flex items-center gap-2 py-2 text-purple-600">
                                                <Loader2 className="animate-spin" size={14} />
                                                <span className="text-xs">Finding available times…</span>
                                            </div>
                                        ) : availableSlots.length > 0 ? (
                                            <>
                                                <p className="text-xs text-purple-700 font-medium mb-1">Available times:</p>
                                                <div className="max-h-32 overflow-y-auto space-y-1">
                                                    {availableSlots.map((slot, idx) => {
                                                        const isSelected = selectedSlot === slot;
                                                        return (
                                                            <button
                                                                key={idx}
                                                                onClick={() => setSelectedSlot(slot)}
                                                                className={`w-full text-left rounded px-2 py-1.5 text-xs border transition-colors ${
                                                                    isSelected
                                                                        ? 'border-green-400 bg-green-50 text-green-800'
                                                                        : 'border-gray-200 bg-white hover:border-purple-300 text-gray-700'
                                                                }`}
                                                            >
                                                                <span className="font-medium">
                                                                    {formatTime(slot.start_time)} – {formatTime(slot.end_time)}
                                                                </span>
                                                                {slot.employee_name && (
                                                                    <span className="text-gray-400 ml-1.5">
                                                                        with {slot.employee_name}
                                                                    </span>
                                                                )}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </>
                                        ) : (
                                            <p className="text-xs text-orange-600 py-2">
                                                No available slots for this date. Try a different suggestion.
                                            </p>
                                        )}

                                        {/* Confirm booking button */}
                                        {selectedSlot && bookingState !== 'success' && (
                                            <button
                                                onClick={handleBookAppointment}
                                                disabled={bookingState === 'booking'}
                                                className={`w-full mt-2 py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${
                                                    bookingState === 'booking'
                                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                        : 'bg-green-600 text-white hover:bg-green-700'
                                                }`}
                                            >
                                                {bookingState === 'booking' ? (
                                                    <>
                                                        <Loader2 className="animate-spin" size={14} />
                                                        Booking…
                                                    </>
                                                ) : (
                                                    <>
                                                        <CheckCircle size={14} />
                                                        Confirm Booking
                                                    </>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ClientAppointmentsPanel;
